import { dispatcher } from 'dispatcher';
import { EventTarget } from 'event-target-shim/es5';
import { useEffect, useRef } from 'react';

import * as hooks from './hooks';
import { PLAYTECH_API_EVENTS } from './constants';

export const useEventsAPI = () => {
    /**
     * This object is just an EventTarget, that allows to subscribe to events.
     * See [MDN docs]{@link https://developer.mozilla.org/en-US/docs/Web/API/EventTarget}.
     * @type {EventTarget}
     */
    const eventTarget = useRef(new EventTarget());
    const handlers = useRef(new WeakMap());
    // eslint-disable-next-line no-underscore-dangle
    const { on = [], once = [] } = window.Playtech._API || {};

    hooks.useOnAppReady(eventTarget.current);
    hooks.useOnIpChange(eventTarget.current);
    hooks.useOnNavigate(eventTarget.current);
    hooks.useOnCookieSettingsUpdated(eventTarget.current);
    hooks.useOnCookieMessageClosed(eventTarget.current);
    hooks.useOnHub2SharedPagesEventsTrack(eventTarget.current);
    hooks.useOnPlayerTagChange(eventTarget.current);
    hooks.useOnRealityCheckPopup(eventTarget.current);
    hooks.useOnGameLaunch(eventTarget.current);
    hooks.useOn2FAPopupClosed(eventTarget.current);
    hooks.useOn2FAPopupOpened(eventTarget.current);
    hooks.useOnUserToggleBalance(eventTarget.current);
    hooks.useOnUserLowBalanceCheck(eventTarget.current);
    hooks.useOnBackButtonToggle(eventTarget.current);
    hooks.useOnSelfExclusion(eventTarget.current);
    hooks.useOnForgotEmail(eventTarget.current);
    hooks.useOnForgotUsername(eventTarget.current);
    hooks.useOnForgotPassword(eventTarget.current);
    hooks.useOnFormStepChanged(eventTarget.current);
    hooks.useOnUserBalanceUpdated(eventTarget.current);
    hooks.useOnLangChange(eventTarget.current);
    hooks.useOnCredentialsLogin(eventTarget.current);
    hooks.useOnCredentialsLoginError(eventTarget.current);
    hooks.useOnGenericLogin(eventTarget.current);
    hooks.useOnGenericLoginError(eventTarget.current);
    hooks.useOnSportsbookAnalytics(eventTarget.current);
    hooks.useOnRegistrationSubmitted(eventTarget.current);
    hooks.useOnRegistrationFail(eventTarget.current);
    hooks.useOnRegistrationSuccess(eventTarget.current);
    hooks.useOnDynamicCustomFormSubmit(eventTarget.current);
    hooks.useOnFavoriteGameToggle(eventTarget.current);
    hooks.useOnLeaderboardMessage(eventTarget.current);
    hooks.useOnLottoAnalytics(eventTarget.current);
    hooks.useOnSocketResponse(eventTarget.current);
    hooks.useOnSocketError(eventTarget.current);
    hooks.useOnSocketBreakdown(eventTarget.current);
    hooks.useOnSocketNotice(eventTarget.current);
    hooks.useOnAdvancedDialog(eventTarget.current);
    hooks.useOnAdvancedDialogNonGame(eventTarget.current);
    hooks.useOnDynamicFormSubmitted(eventTarget.current);
    hooks.useOnDepositLimitChange(eventTarget.current);
    hooks.useOnPASError(eventTarget.current);
    hooks.useOnPASResponse(eventTarget.current);
    hooks.useOnGeofencingInit(eventTarget.current);
    hooks.useOnGeofencingTrigger(eventTarget.current);
    hooks.useOnGeofencingError(eventTarget.current);
    hooks.useOnGeofencingResponse(eventTarget.current);
    hooks.useOnUpdateDetailsFail(eventTarget.current);
    hooks.useOnUpdateDetailsSubmitted(eventTarget.current);
    hooks.useOnUpdateDetailsSuccess(eventTarget.current);

    useEffect(() => {
        window.Playtech = window.Playtech || {};
        window.Playtech = {
            ...window.Playtech,

            /**
             * Shortcut for Playtech.API.events.addEventListener(event, (...args) => { ... }). Example of events see [in events section]{@link Playtech.API.events}.
             * @memberOf Playtech
             * @function
             * @param {string} event
             * @param {EventTarget.EventListener} callback
             * @example
             * Playtech.on(event, (...args) => { ... });
             */
            on: (event, callback) => {
                const handler = ({ detail }) => callback(detail);

                handlers.current.set(callback, handler);
                eventTarget.current.addEventListener(event, handler);
            },

            /**
             * Shortcut for Playtech.API.events.addEventListener(event, (...args) => { ... }, { once: true }).
             * @memberOf Playtech
             * @function
             * @param {string} event
             * @param {EventTarget.EventListener} callback
             * @example
             * Playtech.once(event, (...args) => { ... });
             */
            once: (event, callback) => {
                const handler = ({ detail }) => callback(detail);

                handlers.current.set(callback, handler);
                eventTarget.current.addEventListener(event, handler, { once: true });
            },

            /**
             * Shortcut for Playtech.API.events.removeEventListener(event, callback).
             * @memberOf Playtech
             * @function
             * @param {string} event
             * @param {EventTarget.EventListener} callback
             * @example
             * Playtech.off(event, callback);
             */
            off: (event, callback) => {
                const handler = handlers.current.get(callback);

                eventTarget.current.removeEventListener(event, handler ?? callback);
            }
        };
    }, []);

    useEffect(() => {
        on.forEach(({ event, callback }) => window.Playtech.on(event, callback));
        once.forEach(({ event, callback }) => window.Playtech.once(event, callback));
    }, [
        on,
        once
    ]);

    useEffect(() => {
        Object.keys(PLAYTECH_API_EVENTS)
            .forEach((key) => {
                dispatcher.listen(PLAYTECH_API_EVENTS[key], (...args) => {
                    eventTarget.current.dispatchEvent(new CustomEvent(key, {
                        detail: args
                    }));
                });
            });
    }, []);

    return eventTarget.current;
};
